import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Smartphone, KeyRound, Copy, Check, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Opt-in TOTP two-factor authentication management.
 * Renders inside the Privacy Dashboard "Account security" section.
 */
export const TwoFactorAuth = () => {
  const { user, getAuthHeader, setUser } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.mfa_enabled);
  const [mode, setMode] = useState('idle'); // idle | setup | backup | disable
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null); // { qr_code, secret, otpauth_uri }
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copied, setCopied] = useState(false);

  const reset = () => { setCode(''); setSetupData(null); };

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, getAuthHeader());
      setSetupData(res.data);
      setCode('');
      setMode('setup');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start two-factor setup.');
    } finally { setLoading(false); }
  };

  const confirmEnable = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/enable`, { code: code.trim() }, getAuthHeader());
      setBackupCodes(res.data.backup_codes || []);
      setEnabled(true);
      setUser((u) => (u ? { ...u, mfa_enabled: true } : u));
      setMode('backup');
      toast.success('Two-factor authentication enabled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not enable two-factor authentication.');
    } finally { setLoading(false); }
  };

  const confirmDisable = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: code.trim() }, getAuthHeader());
      setEnabled(false);
      setUser((u) => (u ? { ...u, mfa_enabled: false } : u));
      reset();
      setMode('idle');
      toast.success('Two-factor authentication disabled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not disable two-factor authentication.');
    } finally { setLoading(false); }
  };

  const copyBackup = () => {
    try {
      navigator.clipboard?.writeText(backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="cn-card bg-white dark:bg-[#0F172A] border border-cn-border/70 p-6" data-testid="2fa-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${enabled ? 'bg-cn-success/12 text-cn-success' : 'bg-cn-accent/12 text-cn-accent'}`}>
            {enabled ? <ShieldCheck className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="font-display font-bold text-cn-text dark:text-white">Two-factor authentication</h3>
            <p className="text-sm text-cn-text-muted mt-0.5 max-w-md">
              Add a second step at sign-in using an authenticator app (Google Authenticator, Authy, 1Password).
            </p>
            <span
              className={`inline-block mt-2 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${enabled ? 'bg-cn-success/12 text-cn-success' : 'bg-cn-bone-200 text-cn-text-muted'}`}
              data-testid="2fa-status-badge"
            >
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>

        {mode === 'idle' && (
          enabled ? (
            <button onClick={() => { reset(); setMode('disable'); }} className="cn-btn cn-btn-ghost text-sm text-cn-error" data-testid="2fa-disable-btn">
              Turn off
            </button>
          ) : (
            <button onClick={startSetup} disabled={loading} className="cn-btn cn-btn-primary text-sm" data-testid="2fa-enable-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Smartphone className="w-4 h-4" /> Enable</>}
            </button>
          )
        )}
      </div>

      {/* Setup: show QR + secret + confirm code */}
      {mode === 'setup' && setupData && (
        <div className="mt-6 border-t border-cn-border/60 pt-6 grid md:grid-cols-2 gap-6" data-testid="2fa-setup">
          <div>
            <p className="text-sm font-semibold text-cn-text dark:text-white mb-3">1. Scan this QR code</p>
            <img src={setupData.qr_code} alt="Two-factor QR code" className="w-44 h-44 rounded-xl border border-cn-border bg-white p-2" data-testid="2fa-setup-qr" />
            <p className="text-xs text-cn-text-muted mt-3">Can&rsquo;t scan? Enter this key manually:</p>
            <code className="block mt-1 text-xs font-mono break-all bg-cn-bone-100 dark:bg-white/5 rounded-lg p-2 text-cn-text dark:text-white" data-testid="2fa-setup-secret">{setupData.secret}</code>
          </div>
          <form onSubmit={confirmEnable}>
            <p className="text-sm font-semibold text-cn-text dark:text-white mb-3">2. Enter the 6-digit code</p>
            <Input
              type="text" inputMode="numeric" maxLength={6} autoFocus
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="h-12 rounded-xl text-center text-2xl tracking-[0.4em] font-mono"
              data-testid="2fa-setup-code-input"
            />
            <button type="submit" disabled={loading || code.length !== 6} className="cn-btn cn-btn-primary w-full mt-4 disabled:opacity-60" data-testid="2fa-setup-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Verify & enable</>}
            </button>
            <button type="button" onClick={() => { reset(); setMode('idle'); }} className="cn-btn cn-btn-ghost w-full mt-2 text-sm" data-testid="2fa-setup-cancel-btn">Cancel</button>
          </form>
        </div>
      )}

      {/* Backup codes (shown once after enabling) */}
      {mode === 'backup' && (
        <div className="mt-6 border-t border-cn-border/60 pt-6" data-testid="2fa-backup">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-4 h-4 text-cn-primary" />
            <p className="text-sm font-semibold text-cn-text dark:text-white">Save your backup codes</p>
          </div>
          <p className="text-xs text-cn-text-muted mb-3">Each code works once if you lose your authenticator. Store them somewhere safe — they won&rsquo;t be shown again.</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            {backupCodes.map((c) => (
              <code key={c} className="text-xs font-mono text-center bg-cn-bone-100 dark:bg-white/5 rounded-lg py-1.5 text-cn-text dark:text-white">{c}</code>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={copyBackup} className="cn-btn cn-btn-ghost text-sm" data-testid="2fa-backup-copy-btn">
              {copied ? <><Check className="w-4 h-4 text-cn-success" /> Copied</> : <><Copy className="w-4 h-4" /> Copy codes</>}
            </button>
            <button onClick={() => setMode('idle')} className="cn-btn cn-btn-primary text-sm" data-testid="2fa-backup-done-btn">I&rsquo;ve saved them</button>
          </div>
        </div>
      )}

      {/* Disable: require a code */}
      {mode === 'disable' && (
        <form onSubmit={confirmDisable} className="mt-6 border-t border-cn-border/60 pt-6 max-w-sm" data-testid="2fa-disable">
          <p className="text-sm font-semibold text-cn-text dark:text-white mb-2">Confirm to turn off two-factor</p>
          <p className="text-xs text-cn-text-muted mb-3">Enter a current code from your authenticator app (or a backup code).</p>
          <Input
            type="text" inputMode="text" autoFocus
            value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="123456 or backup code"
            className="h-12 rounded-xl text-center text-lg tracking-[0.2em] font-mono"
            data-testid="2fa-disable-code-input"
          />
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => { reset(); setMode('idle'); }} className="cn-btn cn-btn-ghost text-sm flex-1" data-testid="2fa-disable-cancel-btn">Cancel</button>
            <button type="submit" disabled={loading || !code.trim()} className="cn-btn cn-btn-primary text-sm flex-1 !bg-cn-error hover:!bg-cn-error/90 disabled:opacity-60" data-testid="2fa-disable-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Turn off 2FA'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default TwoFactorAuth;
